import * as React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useScheduleReport } from 'hooks/api/reports';
import { useLoadingOrdersSearchParams } from 'hooks/store/loading-orders';
import { useUnloadingOrdersSearchParams } from 'hooks/store/unloading-orders';
import { PageHeaderWithFilters } from 'components/ui/page-header-with-filters';
import { DataTable } from 'components/ui/DataTable';

import { useColumns, usePageHeaderConfig, useFetchOrders } from './hooks';
import { FiltersForm, CreateOrderButton } from './components';

type Props = {
  orderType: '1' | '2';
};

export const ListOrders = ({ orderType }: Props) => {
  const { checkboxesConfig } = usePageHeaderConfig();
  const { scheduleReport } = useScheduleReport();
  const navigate = useNavigate();
  const columns = useColumns(orderType);

  const loadingOrdersSearchParams = useLoadingOrdersSearchParams();
  const unloadingOrdersSearchParams = useUnloadingOrdersSearchParams();

  const { filters, updateFilters, updateStatuses, clearState } =
    React.useMemo(() => {
      return orderType === '1'
        ? loadingOrdersSearchParams
        : unloadingOrdersSearchParams
    }, [orderType, loadingOrdersSearchParams, unloadingOrdersSearchParams]);

  const reportsUrl = React.useMemo(() => {
    return orderType === '1'
      ? '/relatorios/solicitacoes-de-carregamento'
      : '/relatorios/solicitacoes-de-descarregamento';
  }, [orderType]);

  const { fetchOrders } = useFetchOrders();

  const ordersQuery = useQuery({
    queryKey: ['fetch-orders', filters, orderType],
    queryFn: () => fetchOrders({ filters, orderType }),
  });

  const scheduleReportMutation = useMutation({
    mutationFn: scheduleReport,
    onSuccess: () => {
      toast('Relatório requisitado com sucesso');
      navigate(reportsUrl);
    },
    onError: () => {
      toast.error('Erro ao requisitar relatório');
    },
  });

  const onRequestReport = React.useCallback(
    (reportParams: Record<string, unknown>) => {
      scheduleReportMutation.mutate({
        type: orderType === '1' ? 'loading-orders' : 'unloading-orders',
        params: reportParams,
      });
    },
    [scheduleReportMutation, orderType]
  );

  return (
    <>
      <PageHeaderWithFilters
        checkboxesConfig={checkboxesConfig}
        className={'mb-6'}
      >
        <FiltersForm
          onSubmitFilters={updateFilters}
          onResetFilters={clearState}
          onStatusesChange={updateStatuses}
          onRequestReport={onRequestReport}
          orderType={orderType}
        />
      </PageHeaderWithFilters>
      <DataTable
        columns={columns}
        data={ordersQuery?.data ?? []}
        isLoading={ordersQuery.isLoading}
        actionButton={<CreateOrderButton />}
      />
    </>
  );
};
